import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { ImageUploader } from "./ImageUploader";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

const EMPTY = {
  name: "", category: "", sizes: "", colours: "", construction: "", thickness: "", weight: "",
  backing: "", material: "", pack_size: "", description: "", features: "", applications: "",
  images: [], primary_image: null, published: true, featured: false,
};

const toList = (s) => s.split(",").map((x) => x.trim()).filter(Boolean);
const fromList = (a) => (a || []).join(", ");

export function ProductForm({ open, onOpenChange, product, categories, onSaved }) {
  const init = product ? {
    ...EMPTY, ...product,
    sizes: fromList(product.sizes), colours: fromList(product.colours),
    features: fromList(product.features), applications: fromList(product.applications),
  } : EMPTY;
  const [form, setForm] = useState(init);
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    if (!form.name || !form.category) { toast.error("Name and category are required"); return; }
    setSaving(true);
    const payload = {
      name: form.name, category: form.category,
      sizes: toList(form.sizes), colours: toList(form.colours),
      construction: form.construction, thickness: form.thickness, weight: form.weight,
      backing: form.backing, material: form.material, pack_size: form.pack_size,
      description: form.description, features: toList(form.features), applications: toList(form.applications),
      images: form.images, primary_image: form.primary_image, published: form.published, featured: form.featured,
    };
    try {
      if (product) await api.put(`/admin/products/${product.id}`, payload);
      else await api.post("/admin/products", payload);
      toast.success(product ? "Product updated" : "Product created");
      onSaved();
      onOpenChange(false);
    } catch { toast.error("Failed to save product"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto" data-testid="admin-product-form">
        <DialogHeader>
          <DialogTitle className="font-display text-xl text-[#0B1B3D]">{product ? "Edit Product" : "Add Product"}</DialogTitle>
          <DialogDescription>Fill in product details, colours, sizes and upload images. Dimensions are shown exactly as entered — no area is calculated.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div><Label>Name *</Label><Input data-testid="product-form-name" value={form.name} onChange={set("name")} /></div>
            <div>
              <Label>Category *</Label>
              <Select value={form.category} onValueChange={(v) => setForm((f) => ({ ...f, category: v }))}>
                <SelectTrigger data-testid="product-form-category"><SelectValue placeholder="Select category" /></SelectTrigger>
                <SelectContent>{categories.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div><Label>Description</Label><Textarea value={form.description} onChange={set("description")} rows={2} /></div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div><Label>Sizes (comma-separated)</Label><Input data-testid="product-form-sizes" value={form.sizes} onChange={set("sizes")} placeholder="50cm x 50cm, 2m roll" /></div>
            <div><Label>Colours (comma-separated)</Label><Input data-testid="product-form-colours" value={form.colours} onChange={set("colours")} placeholder="Green, Black, Green-Black" /></div>
          </div>
          <div className="grid gap-4 sm:grid-cols-3">
            <div><Label>Material</Label><Input value={form.material} onChange={set("material")} /></div>
            <div><Label>Construction</Label><Input value={form.construction} onChange={set("construction")} /></div>
            <div><Label>Thickness</Label><Input value={form.thickness} onChange={set("thickness")} /></div>
            <div><Label>Weight</Label><Input value={form.weight} onChange={set("weight")} /></div>
            <div><Label>Backing</Label><Input value={form.backing} onChange={set("backing")} /></div>
            <div><Label>Pack / Carton Size</Label><Input value={form.pack_size} onChange={set("pack_size")} /></div>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div><Label>Features (comma-separated)</Label><Input value={form.features} onChange={set("features")} placeholder="Durable, Anti-slip" /></div>
            <div><Label>Applications (comma-separated)</Label><Input value={form.applications} onChange={set("applications")} placeholder="Offices, Homes" /></div>
          </div>
          <div>
            <Label>Product Images</Label>
            <ImageUploader images={form.images} primary={form.primary_image}
              onChange={(imgs, prim) => setForm((f) => ({ ...f, images: imgs, primary_image: prim }))} />
          </div>
          <div className="flex flex-wrap items-center gap-6">
            <div className="flex items-center gap-3">
              <Switch checked={form.published} onCheckedChange={(v) => setForm((f) => ({ ...f, published: v }))} data-testid="product-form-published" />
              <Label className="cursor-pointer">Published (visible on site)</Label>
            </div>
            <div className="flex items-center gap-3">
              <Switch checked={form.featured} onCheckedChange={(v) => setForm((f) => ({ ...f, featured: v }))} data-testid="product-form-featured" />
              <Label className="cursor-pointer">Featured (Bestsellers row)</Label>
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="product-form-save-btn" className="bg-[#10B981] font-bold hover:bg-[#059669]">
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Save Product
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
